import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import clientPortalService from 'services/clientPortalService';

const CaseTimeline = ({ cases, language, userId }) => {
  const [selectedCase, setSelectedCase] = useState(cases?.[0]?.id);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTimelineEvents = async () => {
      if (!selectedCase) return;
      
      try {
        setLoading(true);
        const events = await clientPortalService?.getCaseTimelineEvents(selectedCase);
        const formattedEvents = events?.map(event => ({
          id: event?.id,
          date: new Date(event?.eventDate)?.toLocaleDateString('en-GB'),
          time: event?.eventTime || 'TBD',
          type: event?.eventType,
          title: event?.title,
          titleSw: event?.title,
          description: event?.description,
          descriptionSw: event?.description,
          status: event?.status,
          lawyer: event?.lawyer?.fullName || 'N/A'
        })) || [];
        setTimelineEvents(formattedEvents);
      } catch (error) {
        console.error('Error fetching timeline events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimelineEvents();
  }, [selectedCase]);

  const translations = {
    en: {
      myCases: "My Cases",
      caseTimeline: "Case Timeline",
      selectCase: "Select a case to view timeline",
      upcoming: "Upcoming",
      completed: "Completed",
      pending: "Pending",
      cancelled: "Cancelled",
      courtDate: "Court Date",
      nextUpdate: "Next Update",
      caseDetails: "Case Details",
      status: "Status",
      assignedLawyer: "Assigned Lawyer",
      caseId: "Case ID"
    },
    sw: {
      myCases: "Kesi Zangu",
      caseTimeline: "Ratiba ya Kesi",
      selectCase: "Chagua kesi ili kuona ratiba",
      upcoming: "Zinazokuja",
      completed: "Zimekamilika",
      pending: "Zinasubiri",
      cancelled: "Zimeghairiwa",
      courtDate: "Tarehe ya Mahakama",
      nextUpdate: "Sasisha Lijalo",
      caseDetails: "Maelezo ya Kesi",
      status: "Hali",
      assignedLawyer: "Wakili Aliyepangiwa",
      caseId: "Kitambulisho cha Kesi"
    }
  };

  const t = translations?.[language];

  const getEventIcon = (type) => {
    switch (type) {
      case 'court':
        return 'Scale';
      case 'document':
        return 'FileText';
      case 'meeting':
        return 'Users';
      case 'deadline':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success-100 text-success-700 border-success-200';
      case 'upcoming':
        return 'bg-primary-100 text-primary-700 border-primary-200';
      case 'pending':
        return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'cancelled':
        return 'bg-error-100 text-error-700 border-error-200';
      default:
        return 'bg-secondary-100 text-secondary-700 border-secondary-200';
    }
  };

  const getTimelineColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success-500';
      case 'upcoming':
        return 'bg-primary-500';
      case 'pending':
        return 'bg-warning-500';
      case 'cancelled':
        return 'bg-error-500';
      default:
        return 'bg-secondary-400';
    }
  };

  const selectedCaseData = cases?.find(c => c?.id === selectedCase);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-text-primary">{t?.myCases}</h1>
      </div>
      {/* Case Selector */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">{t?.selectCase}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {cases?.map((case_) => (
            <div
              key={case_?.id}
              onClick={() => setSelectedCase(case_?.id)}
              className={`
                p-4 rounded-lg border-2 cursor-pointer transition-smooth
                ${selectedCase === case_?.id 
                  ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200 hover:bg-secondary-50'
                }
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-text-primary">{case_?.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  case_?.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                }`}>
                  {case_?.status}
                </span>
              </div>
              <p className="text-sm text-text-secondary mb-1">{t?.caseId}: {case_?.id}</p>
              <p className="text-sm text-text-secondary">{t?.assignedLawyer}: {case_?.lawyer}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Selected Case Details */}
      {selectedCaseData && (
        <div className="bg-surface rounded-lg shadow-card p-6">
          <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">{t?.caseDetails}</h2>
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <div>
              <label className="text-sm font-medium text-text-secondary">{t?.status}</label>
              <p className="text-text-primary font-medium">{selectedCaseData?.status}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">{t?.assignedLawyer}</label>
              <p className="text-text-primary font-medium">{selectedCaseData?.lawyer}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary">{t?.nextUpdate}</label>
              <p className="text-text-primary font-medium">
                {selectedCaseData?.nextCourtDate || "TBD"}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-text-secondary mb-2">
              <span>Case Progress</span>
              <span>{selectedCaseData?.progress}%</span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-3">
              <div 
                className="bg-primary h-3 rounded-full transition-smooth" 
                style={{ width: `${selectedCaseData?.progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}
      {/* Timeline */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <h2 className="text-lg font-heading font-semibold text-text-primary mb-6">{t?.caseTimeline}</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : timelineEvents?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">No timeline events found</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-secondary-200"></div>
            
            <div className="space-y-6">
              {timelineEvents?.map((event, index) => (
                <div key={event?.id} className="relative flex items-start gap-4">
                  {/* Timeline Dot */}
                  <div className={`
                    relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                    ${getTimelineColor(event?.status)}
                  `}>
                    <Icon name={getEventIcon(event?.type)} size={20} color="white" />
                  </div>
                  
                  {/* Event Content */}
                  <div className="flex-1 min-w-0">
                    <div className="bg-secondary-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-text-primary">
                          {language === 'en' ? event?.title : event?.titleSw}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(event?.status)}`}>
                          {t?.[event?.status]}
                        </span>
                      </div>
                      
                      <p className="text-sm text-text-secondary mb-3">
                        {language === 'en' ? event?.description : event?.descriptionSw}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-text-secondary">
                        <span>{event?.date} at {event?.time}</span>
                        <span>{event?.lawyer}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseTimeline;